import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Spinner,
  Alert, Dropdown
} from 'react-bootstrap';
import { 
  FaUsers, 
  FaArrowLeft, FaPaperPlane, FaEllipsisH,
  FaTimes, FaPlus, FaCog, FaPaperclip, FaSearch
} from 'react-icons/fa';
import { useMessaging } from '../hooks/useMessaging';
import { messagingService } from '../services/messagingService';
import { MessageReactions } from './MessageReactions';
import Lightbox from '../../../components/common/Lightbox';
import api from '../../../utils/api';
import {
  sendEncryptedUserMessage,
  assertConversationPeersHaveMessagingKeys,
  formatMissingMessagingKeyHelp,
} from '../../../utils/messageSendE2ee';
import NavigationService from '../../../services/navigationService';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildMessageTimeline, formatTimeLabel } from '../utils/messageTimeline';
import { pinThreadScroller, shouldPinThreadToLatest } from '../utils/messageThreadScroll';
import { 
  getConversationAvatarUrl,
  getConversationTitle,
  getConversationInitials,
  getConversationSubtitle,
  getThreadSubtitle,
  formatConversationTime,
} from '../utils/conversationMeta';
import {
  MSG_FILTER_ALL,
  MSG_LIST_FILTER_CHIPS,
  filterConversations,
} from '../utils/messageListFilters';
import {
  getMessagesView,
  getConversationRouteId,
  messagesListPath,
  messagesNewPath,
  messagesThreadPath,
} from '../utils/messagesRoutes';
import { enrichConversationDisplay } from '../utils/enrichConversationDisplay';
import { useMobileMapLayout } from '../../map/hooks/useMobileMapLayout';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import './MessagesPage.css';
import './messages-shell.css';
import './messages-field-edition.css';
// V2 enabled: force V2 on this page

function RecipientPicker({
  recipientSearchTerm,
  loadingUsers,
  availableUsers,
  candidateUsers,
  selectedParticipants,
  onUserToggle,
}) {
  const query = String(recipientSearchTerm || '').trim();
  // Hide until the user starts typing — avoids the always-on squashed rail.
  if (!query) return null;

  return (
    <div className="msg-buddy-suggestions" data-testid="msg-buddy-suggestions">
      {loadingUsers ? (
        <div className="msg-buddy-suggestions__status">
          <Spinner size="sm" animation="border" />
          <span>Loading buddies…</span>
        </div>
      ) : candidateUsers.length === 0 ? (
        <p className="msg-buddy-suggestions__status msg-buddy-suggestions__status--empty">
          {availableUsers.length === 0
            ? 'No hunting buddies available'
            : `No buddies match "${query}"`}
        </p>
      ) : (
        <>
          <p className="msg-buddy-suggestions__label">Hunting buddies</p>
          <ul className="msg-buddy-suggestions__list">
            {candidateUsers.map((user) => {
              const selected = selectedParticipants.includes(user.id);
              return (
                <li key={user.id}>
                  <button
                    type="button"
                    className={`msg-buddy-suggestions__item${selected ? ' is-selected' : ''}`}
                    onClick={() => onUserToggle(user.id)}
                    aria-pressed={selected}
                    data-testid={`msg-buddy-option-${user.id}`}
                  >
                    <span className="msg-buddy-suggestions__avatar" aria-hidden>
                      {getConversationInitials({
                        conversation_type: 'direct',
                        other_participant: { username: user.username },
                      })}
                    </span>
                    <span className="msg-buddy-suggestions__name">{user.username}</span>
                    <span
                      className={`msg-buddy-suggestions__check${selected ? ' is-checked' : ''}`}
                      aria-hidden
                    >
                      {selected ? '✓' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function NewMessageThread({
  showBack = false,
  onBack,
  recipientSearchTerm,
  onRecipientSearchChange,
  selectedParticipants,
  availableUsers,
  candidateUsers,
  loadingUsers,
  onUserToggle,
  onRemoveParticipant,
  onStartConversation,
  starting = false,
}) {
  const hasRecipient = selectedParticipants.length > 0;

  return (
    <div className="msg-new-message-stack" data-testid="msg-new-message-thread">
      <header className="msg-new-message-header">
        {showBack ? (
          <YButton
            type="button"
            variant="ghost"
            size="sm"
            className="msg-thread-header__back msg-new-message-back"
            onClick={onBack}
            aria-label="Back to conversations"
            data-testid="msg-thread-back"
          >
            <FaArrowLeft />
            {' '}
            Back
          </YButton>
        ) : null}

        <div className="msg-new-message-header__main">
          <h2 className="msg-new-message-header__title">New message</h2>

          <div className="msg-recipient msg-recipient--enamel">
            <span className="msg-recipient__label">Select a recipient</span>
            <div className="msg-recipient__field">
              <input
                type="text"
                className="msg-recipient__input"
                placeholder="Search club members and buddies…"
                value={recipientSearchTerm}
                onChange={(e) => onRecipientSearchChange(e.target.value)}
                aria-label="Recipient search"
                data-testid="msg-recipient-input"
              />
            </div>

            {hasRecipient ? (
              <div className="msg-recipient__chips" data-testid="msg-recipient-chips">
                {selectedParticipants.map((userId) => {
                  const user = availableUsers.find((u) => u.id === userId);
                  if (!user) return null;
                  return (
                    <span key={userId} className="msg-recipient__chip">
                      <span className="msg-recipient__chip-text">{user.username}</span>
                      <button
                        type="button"
                        className="msg-recipient__chip-remove"
                        onClick={() => onRemoveParticipant(userId)}
                        aria-label={`Remove ${user.username}`}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : null}

            <RecipientPicker
              recipientSearchTerm={recipientSearchTerm}
              loadingUsers={loadingUsers}
              availableUsers={availableUsers}
              candidateUsers={candidateUsers}
              selectedParticipants={selectedParticipants}
              onUserToggle={onUserToggle}
            />
          </div>

          <YButton
            type="button"
            variant="primary"
            className="msg-start-conversation-btn"
            onClick={onStartConversation}
            disabled={!hasRecipient || starting}
            data-testid="msg-start-conversation-btn"
          >
            {starting
              ? 'Starting…'
              : `Start conversation · ${selectedParticipants.length}`}
          </YButton>
        </div>
      </header>
    </div>
  );
}

function ConversationListItem({
  conversation,
  isActive,
  onSelect,
  currentUser,
  renderBroadcastPill,
  rowIndex = 0,
}) {
  const avatarUrl = getConversationAvatarUrl(conversation);
  const isGroupLike =
    conversation.conversation_type === 'group' ||
    conversation.conversation_type === 'club_announcement';
  const hasUnread = (conversation.unread_count || 0) > 0;
  const preview = getConversationSubtitle(conversation, currentUser?.id);
  const timeLabel = conversation.last_message
    ? formatConversationTime(conversation.last_message.created_at)
    : '';
  const altClass = rowIndex % 2 === 1 ? ' is-alt' : '';

  return (
    <button
      type="button"
      className={`msg-conv-item${altClass}${isActive ? ' msg-conv-item--active' : ''}${hasUnread ? ' msg-conv-item--unread' : ''}`}
      onClick={() => onSelect(conversation)}
      data-testid={`msg-conv-item-${conversation.id}`}
    >
      <div className="msg-conv-avatar-wrap">
        {avatarUrl ? (
          <div className="msg-conv-avatar">
            <img src={avatarUrl} alt={getConversationTitle(conversation)} />
          </div>
        ) : isGroupLike ? (
          <div className="msg-conv-avatar msg-conv-avatar--group" aria-hidden>
            {getConversationInitials(conversation)}
          </div>
        ) : (
          <div className="msg-conv-avatar" aria-hidden>
            {getConversationInitials(conversation)}
          </div>
        )}
      </div>
      <div className="msg-conv-body">
        <div className="msg-conv-row">
          <div className="msg-conv-name">
            <span className={`msg-conv-name__text${hasUnread ? ' is-unread' : ''}`}>
              {getConversationTitle(conversation)}
            </span>
            {conversation.conversation_type === 'club_announcement' && renderBroadcastPill()}
            {conversation.conversation_type === 'group' ? (
              <span className="msg-group-pill">Group</span>
            ) : null}
          </div>
          {timeLabel ? (
            <span className={`msg-conv-time ${hasUnread ? 'msg-conv-time--unread' : ''}`}>
              {timeLabel}
            </span>
          ) : null}
        </div>
        <div className="msg-conv-preview-row">
          <span className={`msg-conv-preview ${hasUnread ? 'msg-conv-preview--unread' : ''}`}>
            {preview}
          </span>
          {hasUnread ? (
            <span className="msg-conv-unread">{conversation.unread_count}</span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function ConversationListPanel({
  conversations,
  filteredConversations,
  searchTerm,
  onSearchChange,
  listFilter,
  onListFilterChange,
  onStartNewMessage,
  selectedConversation,
  onSelectConversation,
  currentUser,
  renderBroadcastPill,
}) {
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <>
      <div className="msg-list-header">
        <div className="msg-list-header__brand">
          {totalUnread > 0 ? (
            <span className="msg-list-header__badge" data-testid="msg-total-unread">
              {totalUnread}
            </span>
          ) : null}
        </div>
        <YButton
          type="button"
          variant="primary"
          size="sm"
          className="msg-list-header__new-btn"
          onClick={onStartNewMessage}
          aria-label="New message"
          data-testid="msg-new-message-btn"
        >
          <FaPlus aria-hidden="true" />
          {' '}
          New message
        </YButton>
      </div>

      <div className="msg-search-wrap">
        <div className="msg-search">
          <FaSearch className="msg-search__icon" aria-hidden />
          <YInput
            type="search"
            placeholder="Search messages…"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid="msg-search-input"
            aria-label="Search conversations"
          />
        </div>
      </div>

      <div
        className="msg-list-filters"
        role="group"
        aria-label="Conversation filters"
        data-testid="msg-list-filters"
      >
        {MSG_LIST_FILTER_CHIPS.map((chip) => {
          const active = listFilter === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              className={`msg-list-filter-chip${active ? ' is-active' : ''}`}
              aria-pressed={active}
              onClick={() => onListFilterChange(chip.id)}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      <div className="msg-list-scroll">
        {filteredConversations.length > 0 ? (
          <div className="msg-list-panel-card">
            <div className="msg-list-section-label">All Messages</div>
            {filteredConversations.map((conversation, index) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                rowIndex={index}
                isActive={selectedConversation?.id === conversation.id}
                onSelect={onSelectConversation}
                currentUser={currentUser}
                renderBroadcastPill={renderBroadcastPill}
              />
            ))}
          </div>
        ) : (
          <div className="msg-list-empty" data-testid="msg-list-empty">
            <h3 className="msg-list-empty__title">No conversations found</h3>
            <p className="msg-list-empty__text">Clear the search or start a new message.</p>
          </div>
        )}
      </div>
    </>
  );
}

function ConversationAvatar({ conversation, size = 'md' }) {
  const avatarUrl = getConversationAvatarUrl(conversation);
  const isGroupLike =
    conversation?.conversation_type === 'group' ||
    conversation?.conversation_type === 'club_announcement';
  const sizeClass = size === 'sm' ? 'msg-avatar--sm' : 'msg-avatar--md';

  if (avatarUrl) {
    return (
      <div className={`msg-avatar ${sizeClass}`}>
        <img src={avatarUrl} alt={getConversationTitle(conversation)} />
      </div>
    );
  }

  if (isGroupLike) {
    return (
      <div className={`msg-avatar msg-avatar--group ${sizeClass}`} aria-hidden>
        <FaUsers />
      </div>
    );
  }

  return (
    <div className={`msg-avatar ${sizeClass}`} aria-hidden>
      {getConversationInitials(conversation)}
    </div>
  );
}

function ThreadHeader({
  title,
  subtitle,
  conversation,
  showBack = false,
  onBack,
  renderBroadcastPill,
  showBroadcastPill = false,
  onClearMessages,
  menuPlaceholder = false,
}) {
  return (
    <header className="msg-thread-header" data-testid="msg-thread-header">
      {showBack ? (
        <YButton
          type="button"
          variant="ghost"
          size="sm"
          className="msg-thread-header__back"
          onClick={onBack}
          aria-label="Back to conversations"
          data-testid="msg-thread-back"
        >
          <FaArrowLeft />
        </YButton>
      ) : null}

      {conversation ? <ConversationAvatar conversation={conversation} /> : null}

      <div className="msg-thread-header__info">
        <div className="msg-thread-header__title-row">
          <p className="msg-thread-header__title">{title}</p>
          {showBroadcastPill && renderBroadcastPill ? renderBroadcastPill() : null}
        </div>
        {subtitle ? (
          <p className="msg-thread-header__subtitle">{subtitle}</p>
        ) : null}
      </div>

      <div className="msg-thread-header__actions">
        {menuPlaceholder ? (
          <div className="msg-thread-header__menu-placeholder" aria-hidden />
        ) : (
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="link"
              className="msg-thread-header__menu-btn"
              data-testid="msg-thread-menu"
            >
              <FaEllipsisH />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={onClearMessages}>
                <FaCog className="me-2" />
                Clear Messages
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    </header>
  );
}

function MessageDateDivider({ label }) {
  return (
    <div className="msg-date-divider" data-testid="msg-date-divider">
      <div className="msg-date-divider__line" aria-hidden />
      <span className="msg-date-divider__label">{label}</span>
      <div className="msg-date-divider__line" aria-hidden />
    </div>
  );
}

function MessageBubble({
  item,
  isGroup,
  isMobile,
  renderMessageContent,
  onReactionUpdate,
  onAttachmentClick,
}) {
  const { message, meta } = item;
  const isSentByMe = meta.isMine;
  const senderName = message?.sender?.username || 'Member';
  const showSender = !isSentByMe && isGroup && meta.isGroupStart;

  return (
    <div
      className={`msg-bubble-row ${isSentByMe ? 'msg-bubble-row--sent' : 'msg-bubble-row--received'}`}
      data-testid={`msg-bubble-${message.id}`}
    >
      {!isSentByMe && isGroup ? (
        showSender ? (
          <div className="msg-bubble-avatar" aria-hidden>
            {senderName
              .split(/\s+/)
              .map((part) => part[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
        ) : (
          <div className="msg-bubble-avatar-spacer" aria-hidden />
        )
      ) : null}

      <div className={`msg-bubble-col ${isSentByMe ? 'msg-bubble-col--sent' : 'msg-bubble-col--received'}`}>
        {showSender ? (
          <span className="msg-bubble-sender">{senderName}</span>
        ) : null}

        {(message.decrypted_content || (message.attachments && message.attachments.length > 0)) ? (
          <div className={`msg-bubble ${isSentByMe ? 'msg-bubble--sent' : 'msg-bubble--received'}`}>
            {message.decrypted_content ? (
              <div className="msg-bubble__text">
                {renderMessageContent(message.decrypted_content)}
              </div>
            ) : null}
            {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
              <div className="msg-bubble__attachments">
                {message.attachments.map((att, idx) => (
                  <button
                    type="button"
                    key={att.id}
                    className="msg-bubble__attachment-btn"
                    onClick={() => onAttachmentClick(message.attachments, idx)}
                  >
                    <img
                      alt={att.file_name || 'attachment'}
                      src={att.url}
                      className={`msg-bubble__attachment-img ${isMobile ? 'msg-bubble__attachment-img--mobile' : ''}`}
                    />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {meta.showTimestamp ? (
          <div className={`msg-bubble-meta ${isSentByMe ? 'msg-bubble-meta--sent' : ''}`}>
            <span className="msg-bubble-time">{formatTimeLabel(message.created_at)}</span>
          </div>
        ) : null}

        <MessageReactions message={message} onReactionUpdate={onReactionUpdate} />
      </div>
    </div>
  );
}

function MessageTimeline({
  conversationId,
  timeline,
  isGroup,
  isMobile,
  renderMessageContent,
  onReactionUpdate,
  onAttachmentClick,
  messagesEndRef,
}) {
  const scrollRef = useRef(null);
  const pinnedForRef = useRef(null);

  useLayoutEffect(() => {
    if (!shouldPinThreadToLatest({
      conversationId,
      messageCount: timeline.length,
      alreadyPinnedId: pinnedForRef.current,
    })) {
      return;
    }
    pinnedForRef.current = conversationId;
    pinThreadScroller(scrollRef.current);
  }, [conversationId, timeline.length]);

  return (
    <div ref={scrollRef} className="msg-thread-scroll" data-testid="msg-thread-scroll">
      <div className="msg-thread-messages">
        {timeline.map((item) => (
          <React.Fragment key={item.message.id}>
            {item.meta.showDateDivider ? (
              <MessageDateDivider label={item.meta.dateLabel} />
            ) : null}
            <MessageBubble
              item={item}
              isGroup={isGroup}
              isMobile={isMobile}
              renderMessageContent={renderMessageContent}
              onReactionUpdate={onReactionUpdate}
              onAttachmentClick={onAttachmentClick}
            />
          </React.Fragment>
        ))}
        <div ref={messagesEndRef} className="msg-thread-scroll__anchor" />
      </div>
    </div>
  );
}

function ComposeBar({
  value,
  onChange,
  onKeyDown,
  onSend,
  onAttachClick,
  onFileChange,
  fileInputRef,
  inputRef,
  disabled = false,
  sending = false,
  canSend,
  pendingFiles = [],
  onRemovePendingFile,
  showAttach = true,
  showHint = true,
}) {
  const sendDisabled = disabled || sending || !canSend;

  return (
    <div className="msg-compose" data-testid="msg-compose-bar">
      {pendingFiles.length > 0 ? (
        <div className="msg-compose__preview">
          {pendingFiles.map((file, idx) => (
            <div className="msg-compose__preview-chip" key={`${file.name}-${idx}`}>
              <FaPaperclip aria-hidden />
              <span className="msg-compose__preview-name">{file.name}</span>
              <button
                type="button"
                className="msg-compose__preview-remove"
                aria-label={`Remove ${file.name}`}
                onClick={() => onRemovePendingFile(idx)}
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="msg-compose__row">
        {showAttach ? (
          <YButton
            type="button"
            variant="ghost"
            size="sm"
            className="msg-compose__attach"
            onClick={onAttachClick}
            disabled={disabled}
            aria-label="Attach photos"
            title="Attach photos"
            data-testid="msg-compose-attach"
          >
            <FaPaperclip aria-hidden="true" />
          </YButton>
        ) : null}

        <textarea
          ref={inputRef}
          className="msg-compose__input"
          rows={1}
          placeholder="Write a message..."
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          data-testid="msg-compose-input"
          aria-label="Message"
        />

        <YButton
          type="button"
          variant="primary"
          size="sm"
          className="msg-compose__send"
          onClick={onSend}
          disabled={sendDisabled}
          aria-label="Send message"
          title="Send message"
          data-testid="msg-compose-send"
        >
          {sending ? <Spinner size="sm" animation="border" /> : (
            <FaPaperPlane aria-hidden="true" />
          )}
        </YButton>
      </div>

      {showHint ? (
        <p className="msg-compose__hint" data-testid="msg-compose-e2ee">
          End-to-end encrypted · Enter to send
        </p>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="msg-compose__file-input"
        onChange={onFileChange}
        tabIndex={-1}
        aria-hidden
      />
    </div>
  );
}

export function MessagesPage() {
  const isV2 = true;
  const isMobile = useMobileMapLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const urlView = getMessagesView(location.pathname);
  // Local panel is source of truth for UI so drills open immediately (URL stays in sync).
  const [panel, setPanel] = useState(urlView.view);
  const [panelConversationId, setPanelConversationId] = useState(urlView.conversationId);
  const stackView = panel;
  const conversationIdParam = panelConversationId;
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [listFilter, setListFilter] = useState(MSG_FILTER_ALL);
  const [newConversationName, setNewConversationName] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [startingConversation, setStartingConversation] = useState(false);
  const [recipientSearchTerm, setRecipientSearchTerm] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = React.useRef(null);
  const messageInputRef = React.useRef(null);
  const newMessageInputRef = React.useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  
  const {
    conversations,
    selectedConversation,
    messages,
    loading,
    sending,
    error,
    // unreadCounts,
    messagesEndRef,
    currentUser,
    sendMessage,
    createConversation,
    findDirectMessage,
    selectConversation,
    clearError,
    loadMessages,
    // deleteMessage,
    // deleteConversation,
  } = useMessaging();

  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const handledNavigationKeyRef = useRef(null);

  // Load available users helper (after currentUser is declared)
  const loadAvailableUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      // Gate suggestions to buddies only
      const response = await api.get('/buddies/');
      const users = (response.data?.results || response.data || []).map(u => ({ id: u.id, username: u.username })) || [];
      const meId = currentUser ? Number(currentUser.id) : null;
      const filteredUsers = users.filter(user => user && user.id && user.username && (meId == null || Number(user.id) !== meId));
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [currentUser]);

  // Keep local panel aligned with URL when the path actually changes (back/forward + deep links).
  // Do not depend on derived urlView alone during optimistic setPanel + navigate.
  useEffect(() => {
    const next = getMessagesView(location.pathname);
    setPanel(next.view);
    setPanelConversationId(next.conversationId);
  }, [location.pathname]);

  // Load users when creating a new message
  useEffect(() => {
    if (stackView === 'new' && availableUsers.length === 0 && !loadingUsers) {
      loadAvailableUsers();
    }
  }, [stackView, availableUsers.length, loadingUsers, loadAvailableUsers]);

  // Sync stack panel → selected conversation
  useEffect(() => {
    if (stackView === 'list') {
      if (selectedConversation) selectConversation(null);
      return;
    }

    if (stackView !== 'thread' || !conversationIdParam) return;

    if (getConversationRouteId(selectedConversation) === String(conversationIdParam)) return;

    let cancelled = false;
    (async () => {
      try {
        const existing = conversationsRef.current.find(
          (c) => getConversationRouteId(c) === String(conversationIdParam)
        );
        const conversation =
          existing || (await messagingService.getConversation(conversationIdParam));
        if (!cancelled && conversation) {
          selectConversation(conversation);
        }
      } catch (error) {
        console.error('Failed to open conversation from route:', error);
        if (!cancelled) {
          setPanel('list');
          setPanelConversationId(null);
          navigate(messagesListPath(), { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    stackView,
    conversationIdParam,
    selectedConversation,
    selectConversation,
    navigate,
  ]);

  // Handle navigation state for pre-selecting users (e.g. from club members)
  const applyMessagesNavigationState = useCallback(() => {
    const navigationState = NavigationService.getNavigationState(location);
    const { action, recipientId, recipientName } = navigationState;
    if (!action) return;

    const navKey = `${location.key}:${action}:${recipientId || ''}`;
    if (handledNavigationKeyRef.current === navKey) return;
    handledNavigationKeyRef.current = navKey;

    if (action === 'new_message' && recipientId) {
      NavigationService.clearNavigationState(navigate, messagesNewPath());
      const checkExistingConversation = async () => {
        try {
          const existingConversation = await findDirectMessage(recipientId);

          if (existingConversation && existingConversation.conversation) {
            const conv = existingConversation.conversation;
            const routeId = getConversationRouteId(conv);
            setPanel('thread');
            setPanelConversationId(routeId);
            selectConversation(conv);
            navigate(messagesThreadPath(routeId), { replace: true });
            return;
          }

          setSelectedParticipants([recipientId]);
          setRecipientSearchTerm(recipientName || '');
          if (availableUsers.length === 0) {
            loadAvailableUsers();
          }
        } catch (error) {
          console.error('Failed to check for existing conversation:', error);
          setSelectedParticipants([recipientId]);
          setRecipientSearchTerm(recipientName || '');
          if (availableUsers.length === 0) {
            loadAvailableUsers();
          }
        }
      };

      checkExistingConversation();
    }
  }, [location, availableUsers.length, findDirectMessage, loadAvailableUsers, navigate, selectConversation]);

  useEffect(() => {
    applyMessagesNavigationState();
  }, [applyMessagesNavigationState]);

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const clamped = Math.min(el.scrollHeight, 180);
    el.style.height = `${clamped}px`;
  };

  const handleMessageChange = (setter) => (e) => {
    setter(e.target.value);
    autoResize(e.target);
  };

  useEffect(() => {
    autoResize(messageInputRef.current);
    autoResize(newMessageInputRef.current);
  }, [newMessage]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversation) return;

    if (pendingFiles && pendingFiles.length > 0) {
      try {
        const caption = newMessage.trim();
        const created = await sendEncryptedUserMessage(
          messagingService,
          selectedConversation,
          caption || '',
          'image'
        );
        const attachments = [];
        for (const file of pendingFiles.slice(0, 2)) {
          const initRes = await api.post('/assets/init/', {
            filename: file.name,
            content_type: file.type || 'image/jpeg',
            scope: 'message-photo',
            public: false
          });
          const { upload_url, object_key } = initRes.data || {};
          if (!upload_url || !object_key) continue;
          await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': file.type || 'image/jpeg' }, body: file });
          attachments.push({ object_key, file_name: file.name, file_size: file.size, content_type: file.type || 'image/jpeg' });
        }
        if (attachments.length > 0) {
          await messagingService.attachMessagePhotos(created.id, attachments);
        }
        setPendingFiles([]);
        setNewMessage('');
        await loadMessages(selectedConversation.id);
      } catch (err) {
        console.error('Attach photos failed', err);
        alert(err?.response?.data?.error || 'Failed to attach photos');
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return;
    }

    if (!newMessage.trim()) return;
    const messageContent = newMessage.trim();
    setNewMessage('');
    await sendMessage(messageContent);
  };

  const openListPanel = useCallback(() => {
    setPanel('list');
    setPanelConversationId(null);
    setSelectedParticipants([]);
    setRecipientSearchTerm('');
    setNewConversationName('');
    navigate(messagesListPath());
  }, [navigate]);

  const openThreadPanel = useCallback((conversation) => {
    const routeId = getConversationRouteId(conversation);
    if (!routeId) return;
    setSelectedParticipants([]);
    setRecipientSearchTerm('');
    setPanel('thread');
    setPanelConversationId(routeId);
    selectConversation(conversation);
    navigate(messagesThreadPath(routeId));
  }, [navigate, selectConversation]);

  const handleStartNewMessage = () => {
    setSelectedParticipants([]);
    setRecipientSearchTerm('');
    setNewConversationName('');
    setPanel('new');
    setPanelConversationId(null);
    navigate(messagesNewPath());
  };

  const handleSelectConversation = useCallback((conversation) => {
    openThreadPanel(conversation);
  }, [openThreadPanel]);

  const handleCancelNewMessage = () => {
    openListPanel();
  };

  const handleBackToList = useCallback(() => {
    openListPanel();
  }, [openListPanel]);

  // Filter users based on search term (keep selected visible for toggle)
  const candidateUsers = availableUsers.filter((user) => {
    if (!user || !user.username) return false;
    if (!recipientSearchTerm.trim()) return true;
    return user.username.toLowerCase().includes(recipientSearchTerm.toLowerCase());
  });

  // removed unused delete handlers

  const handleClearMessages = async () => {
    if (!selectedConversation) return;
    if (!window.confirm('Clear messages up to now from your view? This does not delete for others.')) return;
    try {
      await messagingService.clearConversationUntilNow(selectedConversation.id);
      await loadMessages(selectedConversation.id);
    } catch (e) {
      alert('Failed to clear messages');
    }
  };

  const handleStartConversation = async () => {
    if (selectedParticipants.length === 0) return;

    const conversationType = selectedParticipants.length === 1 ? 'direct' : 'group';
    const meId = currentUser ? Number(currentUser.id) : null;
    if (!meId) {
      alert('You need to be signed in to send messages.');
      return;
    }

    const peerIdsForKeys =
      conversationType === 'direct'
        ? [meId, Number(selectedParticipants[0])]
        : [meId, ...selectedParticipants.map((id) => Number(id))];

    setStartingConversation(true);
    try {
      await assertConversationPeersHaveMessagingKeys(messagingService, peerIdsForKeys);
    } catch (e) {
      const help = formatMissingMessagingKeyHelp(e, {
        currentUserId: currentUser?.id,
        usernameForUserId: (uid) => availableUsers.find((u) => Number(u.id) === uid)?.username,
      });
      alert(help || 'Some participants are not ready for encrypted messaging yet.');
      setStartingConversation(false);
      return;
    }

    try {
      if (conversationType === 'direct') {
        const existing = await findDirectMessage(selectedParticipants[0]);
        const existingConversation = existing?.conversation || existing;
        if (getConversationRouteId(existingConversation)) {
          openThreadPanel(
            enrichConversationDisplay(existingConversation, {
              selectedParticipantIds: selectedParticipants,
              availableUsers,
              currentUserId: currentUser?.id,
            })
          );
          return;
        }
      }

      const autoName =
        conversationType === 'group'
          ? selectedParticipants
              .map((id) => availableUsers.find((u) => u.id === id)?.username)
              .filter(Boolean)
              .join(', ')
          : undefined;

      const conversationData = {
        conversation_type: conversationType,
        participant_ids: selectedParticipants,
        name: (newConversationName.trim() || autoName) || undefined,
      };

      const newConversation = await createConversation(conversationData);
      if (getConversationRouteId(newConversation)) {
        openThreadPanel(
          enrichConversationDisplay(newConversation, {
            selectedParticipantIds: selectedParticipants,
            availableUsers,
            currentUserId: currentUser?.id,
          })
        );
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
      const apiMsg = error?.response?.data || error?.message;
      const text = typeof apiMsg === 'string' ? apiMsg : (apiMsg?.detail || apiMsg?.error || JSON.stringify(apiMsg));
      if (text && String(text).toLowerCase().includes('only allowed between hunting buddies')) {
        alert('You can only send direct messages to hunting buddies. Send a buddy request first.');
      } else if (text && String(text).toLowerCase().includes('must have exactly one participant')) {
        alert('Direct messages must have exactly one recipient.');
      } else {
        alert('Could not create conversation. Please try again.');
      }
    } finally {
      setStartingConversation(false);
    }
  };

  const handleParticipantToggle = (userId) => {
    setSelectedParticipants(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
    setRecipientSearchTerm('');
  };

  const filteredConversations = useMemo(
    () =>
      filterConversations(conversations, {
        searchTerm,
        filterId: listFilter,
        currentUserId: currentUser?.id,
        getTitle: getConversationTitle,
        getSubtitle: getConversationSubtitle,
      }),
    [conversations, searchTerm, listFilter, currentUser?.id],
  );

  const messageTimeline = useMemo(
    () => buildMessageTimeline(messages, currentUser?.id),
    [messages, currentUser]
  );

  const isGroupConversation =
    selectedConversation?.conversation_type === 'group';

  const handleAttachmentClick = useCallback((attachments, idx) => {
    const items = attachments.map((a) => ({
      caption: a.file_name || '',
      url: a.url,
      getUrl: async () => a.url,
    }));
    setLightboxItems(items);
    setLightboxIndex(idx);
    setLightboxOpen(true);
  }, []);

  const handleReactionUpdate = useCallback(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation, loadMessages]);

  const handleFileInputChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 2) {
      alert('You can attach up to 2 photos per message.');
    }
    setPendingFiles(files.slice(0, 2));
  }, []);

  const handleRemoveParticipant = useCallback((userId) => {
    setSelectedParticipants((prev) => prev.filter((id) => id !== userId));
  }, []);

  const handleRemovePendingFile = useCallback((idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const newMessageThreadProps = {
    recipientSearchTerm,
    onRecipientSearchChange: setRecipientSearchTerm,
    selectedParticipants,
    availableUsers,
    candidateUsers,
    loadingUsers,
    onUserToggle: handleParticipantToggle,
    onRemoveParticipant: handleRemoveParticipant,
    onStartConversation: handleStartConversation,
    starting: startingConversation,
  };

  const renderBroadcastPill = () => (
    <span className="msg-broadcast-pill">
      ◈ Broadcast
    </span>
  );

  // Add this function before renderMessage
  const renderMessageContent = (content) => {
    if (!content) return null;
    
    // Convert markdown-style links to clickable links
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    
    if (linkRegex.test(content)) {
      // Replace markdown links with actual HTML links
      const htmlContent = content.replace(linkRegex, (match, linkText, url) => {
        return `<a href="${url}" class="message-link">${linkText}</a>`;
      });
      
      return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }
    
    return <div>{content}</div>;
  };

  if (loading) {
    return (
      <div className="messages-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="messages-error">
        <Alert variant="danger" onClose={clearError} dismissible>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  let stackContent = null;

  if (stackView === 'new') {
    stackContent = (
      <div
        className={`msg-shell msg-shell--stack messaging-v2 ${isV2 ? 'messaging-v2' : ''}`}
        data-testid="messages-page"
        data-msg-view="new"
      >
        <div className="msg-thread-panel msg-thread-panel--visible msg-thread-panel--stack">
          <NewMessageThread
            showBack
            onBack={handleCancelNewMessage}
            {...newMessageThreadProps}
          />
        </div>
      </div>
    );
  } else if (stackView === 'thread') {
    stackContent = (
      <div
        className={`msg-shell msg-shell--stack messaging-v2 ${isV2 ? 'messaging-v2' : ''}`}
        data-testid="messages-page"
        data-msg-view="thread"
      >
        <div className="msg-thread-panel msg-thread-panel--visible msg-thread-panel--stack">
          {selectedConversation ? (
            <>
              <ThreadHeader
                conversation={selectedConversation}
                title={getConversationTitle(selectedConversation)}
                subtitle={getThreadSubtitle(selectedConversation)}
                showBack
                onBack={handleBackToList}
                showBroadcastPill={selectedConversation.conversation_type === 'club_announcement'}
                renderBroadcastPill={renderBroadcastPill}
                onClearMessages={handleClearMessages}
              />

              <MessageTimeline
                conversationId={selectedConversation.id}
                timeline={messageTimeline}
                isGroup={isGroupConversation}
                isMobile={isMobile}
                renderMessageContent={renderMessageContent}
                onReactionUpdate={handleReactionUpdate}
                onAttachmentClick={handleAttachmentClick}
                messagesEndRef={messagesEndRef}
              />

              <ComposeBar
                value={newMessage}
                onChange={handleMessageChange(setNewMessage)}
                onKeyDown={handleKeyPress}
                onSend={handleSendMessage}
                onAttachClick={() => fileInputRef.current && fileInputRef.current.click()}
                onFileChange={handleFileInputChange}
                fileInputRef={fileInputRef}
                inputRef={messageInputRef}
                sending={sending}
                canSend={Boolean(newMessage.trim()) || pendingFiles.length > 0}
                pendingFiles={pendingFiles}
                onRemovePendingFile={handleRemovePendingFile}
              />
            </>
          ) : (
            <div className="messages-loading" data-testid="msg-thread-loading">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading conversation...</span>
              </Spinner>
            </div>
          )}
        </div>
      </div>
    );
  } else {
    stackContent = (
      <div
        className={`msg-shell msg-shell--stack messaging-v2 ${isV2 ? 'messaging-v2' : ''}`}
        data-testid="messages-page"
        data-msg-view="list"
      >
        <aside className="msg-list-panel msg-list-panel--stack">
          <ConversationListPanel
            conversations={conversations}
            filteredConversations={filteredConversations}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            listFilter={listFilter}
            onListFilterChange={setListFilter}
            onStartNewMessage={handleStartNewMessage}
            selectedConversation={selectedConversation}
            onSelectConversation={handleSelectConversation}
            currentUser={currentUser}
            renderBroadcastPill={renderBroadcastPill}
          />
        </aside>
      </div>
    );
  }

  return (
    <>
      {stackContent}
      {lightboxOpen && (
        <Lightbox
          items={lightboxItems}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
